/**
 * feeTree/buildFeeTree.ts
 * Pure utility function to transform BrokerFeeConfiguration[] into hierarchical FeeTreeNode[]
 *
 * Builds a sparse tree following AssetClass taxonomy:
 * Asset Class Type → Market Segment → Sub-Asset Class → Instrument
 *
 * Only nodes with direct fees OR descendants with fees are included.
 * Follows parametric pattern: accepts display maps as parameter per CODING_STANDARDS.
 */

import type { BrokerFeeConfiguration } from '@/types/schema/Broker';
import type { AssetClass } from '@/types/shared/AssetClass';
import type { FeeTreeNode, FeeTreeNodeType, DisplayMaps } from './types';

// -----------------------------------------------------------------------------
// Internal Types
// -----------------------------------------------------------------------------

/** Intermediate node structure during tree building */
interface BuildNode {
  id: string;
  type: FeeTreeNodeType;
  label: string;
  value: string;
  categoryPath: string[];
  depth: number;
  feeConfigIds: string[];
  children: Map<string, BuildNode>;
  instruments: Map<string, BuildNode>;
}

// -----------------------------------------------------------------------------
// Helper Functions
// -----------------------------------------------------------------------------

/**
 * Extract category path segments from AssetClass
 * Returns array of [assetClass, marketSegment, subAssetClass?]
 */
function getCategoryPath(category: AssetClass): string[] {
  const path: string[] = [category.assetClass, category.marketSegment];
  if (category.subAssetClass) {
    path.push(category.subAssetClass);
  }
  return path;
}

/**
 * Generate stable node ID for a category path
 * Format: `category:{path}` where path uses `/` separator
 */
function getCategoryNodeId(path: string[]): string {
  return `category:${path.join('/')}`;
}

/**
 * Generate stable node ID for an instrument under a category
 * Format: `category:{path}/instrument:{instrumentId}`
 */
function getInstrumentNodeId(categoryPath: string[], instrumentId: string): string {
  if (categoryPath.length === 0) {
    return `instrument:${instrumentId}`;
  }
  return `category:${categoryPath.join('/')}/instrument:${instrumentId}`;
}

/**
 * Get display label for a category node at given depth
 */
function getCategoryLabel(
  value: string,
  depth: number,
  displayMaps: DisplayMaps
): string {
  switch (depth) {
    case 0:
      return displayMaps.assetClassType[value as keyof typeof displayMaps.assetClassType] ?? value;
    case 1:
      return displayMaps.marketSegment[value as keyof typeof displayMaps.marketSegment] ?? value;
    case 2:
      return displayMaps.subAssetClass[value as keyof typeof displayMaps.subAssetClass] ?? value;
    default:
      return value;
  }
}

/**
 * Get FeeTreeNodeType for a category at given depth
 */
function getCategoryType(depth: number): FeeTreeNodeType {
  switch (depth) {
    case 0:
      return 'asset-class';
    case 1:
      return 'market-segment';
    case 2:
      return 'sub-asset-class';
    default:
      return 'sub-asset-class';
  }
}

/**
 * Create or get a category node in the tree, creating parent nodes as needed
 * @throws Error if categoryPath is empty (should never happen with valid AssetClass)
 */
function ensureCategoryNode(
  root: Map<string, BuildNode>,
  categoryPath: string[],
  displayMaps: DisplayMaps
): BuildNode {
  if (categoryPath.length === 0) {
    throw new Error('ensureCategoryNode called with empty path');
  }

  let currentLevel = root;
  let lastNode: BuildNode | undefined;

  categoryPath.forEach((value, i) => {
    const pathSoFar = categoryPath.slice(0, i + 1);
    const nodeId = getCategoryNodeId(pathSoFar);

    let node = currentLevel.get(value);
    if (!node) {
      node = {
        id: nodeId,
        type: getCategoryType(i),
        label: getCategoryLabel(value, i, displayMaps),
        value,
        categoryPath: pathSoFar,
        depth: i,
        feeConfigIds: [],
        children: new Map(),
        instruments: new Map(),
      };
      currentLevel.set(value, node);
    }
    lastNode = node;
    currentLevel = node.children;
  });

  // lastNode is guaranteed to be defined since categoryPath.length > 0 and forEach ran at least once
  if (!lastNode) {
    throw new Error('Unexpected: no node created for non-empty path');
  }
  return lastNode;
}

/**
 * Convert BuildNode to FeeTreeNode, recursively processing children
 * Only includes nodes that have direct fees OR descendants with fees
 */
function convertToTreeNode(buildNode: BuildNode): FeeTreeNode | null {
  // Recursively convert children (categories)
  const childNodes: FeeTreeNode[] = [];
  for (const child of buildNode.children.values()) {
    const converted = convertToTreeNode(child);
    if (converted) {
      childNodes.push(converted);
    }
  }

  // Convert instruments
  const instrumentNodes: FeeTreeNode[] = [];
  for (const instrument of buildNode.instruments.values()) {
    // Instruments always have direct fees, so always include
    instrumentNodes.push({
      id: instrument.id,
      type: 'instrument',
      label: instrument.label,
      value: instrument.value,
      categoryPath: instrument.categoryPath,
      depth: instrument.depth,
      hasDirectFee: true,
      feeConfigIds: instrument.feeConfigIds,
      children: [],
    });
  }

  // Check if this node should be included (has direct fee or has children/instruments)
  const hasDirectFee = buildNode.feeConfigIds.length > 0;
  const hasDescendants = childNodes.length > 0 || instrumentNodes.length > 0;

  if (!hasDirectFee && !hasDescendants) {
    return null;
  }

  // Sort: categories alphabetically by label, then instruments alphabetically by label
  childNodes.sort((a, b) => a.label.localeCompare(b.label));
  instrumentNodes.sort((a, b) => a.label.localeCompare(b.label));

  return {
    id: buildNode.id,
    type: buildNode.type,
    label: buildNode.label,
    value: buildNode.value,
    categoryPath: buildNode.categoryPath,
    depth: buildNode.depth,
    hasDirectFee,
    feeConfigIds: buildNode.feeConfigIds,
    children: [...childNodes, ...instrumentNodes],
  };
}

// -----------------------------------------------------------------------------
// Main Export
// -----------------------------------------------------------------------------

/**
 * Build a hierarchical fee tree from broker fee configurations
 *
 * @param configs - Array of BrokerFeeConfiguration from selectedBroker
 * @param displayMaps - Display maps for converting raw values to labels
 * @returns Sorted array of root FeeTreeNode (sparse tree with only nodes that have fees)
 *
 * @example
 * ```ts
 * import {
 *   ASSET_CLASS_TYPE_DISPLAY_MAP,
 *   MARKET_SEGMENT_DISPLAY_MAP,
 *   SUB_ASSET_CLASS_DISPLAY_MAP
 * } from '@/constants/assetClass';
 *
 * const tree = buildFeeTree(selectedBroker.feeConfigurations, {
 *   assetClassType: ASSET_CLASS_TYPE_DISPLAY_MAP,
 *   marketSegment: MARKET_SEGMENT_DISPLAY_MAP,
 *   subAssetClass: SUB_ASSET_CLASS_DISPLAY_MAP,
 * });
 * ```
 */
export function buildFeeTree(
  configs: BrokerFeeConfiguration[],
  displayMaps: DisplayMaps
): FeeTreeNode[] {
  // Handle empty input
  if (!configs || configs.length === 0) {
    return [];
  }

  // Build intermediate tree structure
  const root = new Map<string, BuildNode>();

  // Root-level instruments (configs with instrumentId but no category)
  const rootInstruments = new Map<string, BuildNode>();

  for (const config of configs) {
    // Skip archived configs
    if (config.status === 'archived') {
      continue;
    }

    const { category, instrumentId } = config;

    if (category) {
      // Ensure category nodes exist
      const categoryPath = getCategoryPath(category);
      const categoryNode = ensureCategoryNode(root, categoryPath, displayMaps);

      if (instrumentId) {
        // Instrument under category
        let instrumentNode = categoryNode.instruments.get(instrumentId);
        if (!instrumentNode) {
          instrumentNode = {
            id: getInstrumentNodeId(categoryPath, instrumentId),
            type: 'instrument',
            label: instrumentId, // Use instrumentId as label (e.g., 'MNQ')
            value: instrumentId,
            categoryPath,
            depth: categoryPath.length,
            feeConfigIds: [],
            children: new Map(),
            instruments: new Map(),
          };
          categoryNode.instruments.set(instrumentId, instrumentNode);
        }
        instrumentNode.feeConfigIds.push(config.id);
      } else {
        // Category-level fee
        categoryNode.feeConfigIds.push(config.id);
      }
    } else if (instrumentId) {
      // Uncategorized instrument (no category defined)
      // Uses 'unknown' as category path for consistent ID namespacing
      let instrumentNode = rootInstruments.get(instrumentId);
      if (!instrumentNode) {
        instrumentNode = {
          id: getInstrumentNodeId(['unknown'], instrumentId),
          type: 'instrument',
          label: instrumentId,
          value: instrumentId,
          categoryPath: ['unknown'],
          depth: 0, // Still at root level visually (no "Unknown" category node)
          feeConfigIds: [],
          children: new Map(),
          instruments: new Map(),
        };
        rootInstruments.set(instrumentId, instrumentNode);
      }
      instrumentNode.feeConfigIds.push(config.id);
    }
    // Configs with neither category nor instrument are ignored
  }

  // Convert to final tree structure
  const result: FeeTreeNode[] = [];

  // Convert category nodes
  for (const buildNode of root.values()) {
    const treeNode = convertToTreeNode(buildNode);
    if (treeNode) {
      result.push(treeNode);
    }
  }

  // Convert root-level instruments
  for (const instrument of rootInstruments.values()) {
    result.push({
      id: instrument.id,
      type: 'instrument',
      label: instrument.label,
      value: instrument.value,
      categoryPath: [],
      depth: 0,
      hasDirectFee: true,
      feeConfigIds: instrument.feeConfigIds,
      children: [],
    });
  }

  // Sort root level: categories first (alphabetically), then instruments (alphabetically)
  result.sort((a, b) => {
    // Categories before instruments
    if (a.type !== 'instrument' && b.type === 'instrument') return -1;
    if (a.type === 'instrument' && b.type !== 'instrument') return 1;
    // Alphabetically by label within same group
    return a.label.localeCompare(b.label);
  });

  return result;
}
